import React from "react";
import { NodeProps, useReactFlow } from "reactflow";
import axios from "axios";
import * as fabric from 'fabric';
import { Select } from "../../../../form/select";
import { operationType, selectDataType } from "../../../../../services/types";
import { updateNodeValue } from "../../../../../services/method";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faComments } from "@fortawesome/free-regular-svg-icons";
import { faCloudArrowUp, faCrop, faCropSimple, faMinus, faPlus, faRotateRight, faTimes } from "@fortawesome/free-solid-svg-icons";
import { Main } from "./style";
import { webURL } from "../../../../../services/API";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../redux/store";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";

type OCRProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};

const CANVAS_WIDTH = 833, CANVAS_HEIGHT = 772;

export function OCR({ id, data, setChildMinimize, setChildSettings }: OCRProps) {
   const userToken = useSelector((state: RootState) => state.user.data?.token);
   const { setNodes } = useReactFlow();
   const [types, setTypes] = React.useState<selectDataType[]>([
      { id: 1, name: 'Image', refId: 'image', isActive: true }, { id: 2, name: 'Document', refId: 'document' }, 
      { id: 3, name: 'Check Id', refId: 'check_id' },
   ]);
   const [canvas, setCanvas] = React.useState<fabric.Canvas | null>(null);
   const [annotations, setAnnotations] = React.useState({ isCanDrawing: false, isDrawing: false });
   const [rect, setRect] = React.useState<any>(null);
   const [startX, setStartX] = React.useState(0);
   const [startY, setStartY] = React.useState(0);
   const inputRef = React.useRef<HTMLInputElement>(null);
   const canvasRef = React.useRef<HTMLCanvasElement>(null);
   const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
      
   // --
   React.useEffect(() => {
      if (setChildMinimize) {
         setChildMinimize(() => {
            updateNodeValue(id, setNodes, { 
               isNoMinimize: !data.value?.isNoMinimize, isAnnotationsToggle: data.value?.type === 'check_id' ? !data.value?.isNoMinimize : false 
            })
         });
      }
      if (setChildSettings) {
         setChildSettings(() => {
            setSettings(prev => ({ ...prev, isToggle: !prev.isToggle }));
         });
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setChildMinimize, setChildSettings]);

   // ---
   React.useEffect(() => {
      if (!data.value) updateNodeValue(id, setNodes, { type: types[0].refId, annotationsLabel: 'Unlabeled' });
      else {
         updateNodeValue(id, setNodes, { isNoMinimize: false, isAnnotationsToggle: false });
         setTypes(types => types.map(type => ({ ...type, isActive: type.refId === data.value.type })));
      }
      // canvas
      if (canvasRef.current) {
         const initCanvas = new fabric.Canvas(canvasRef.current, { width: CANVAS_WIDTH, height: CANVAS_HEIGHT });
         canvasRef.current.width = CANVAS_WIDTH;
         canvasRef.current.height = CANVAS_HEIGHT;
         setCanvas(initCanvas);
         return () => { initCanvas.dispose() };
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);
   // ---
   React.useEffect(() => {
      if (canvas && data.value?.isAnnotationsFile) {
         if (!data.value.annotationsUrl) {
            updateNodeValue(id, setNodes, { canvasJSON: null, annotationsUrl: '', isAnnotationsFile: false, canvasURLimg: null });
         } else if (data.value.canvasJSON) {
            canvas.loadFromJSON(data.value.canvasJSON, () => {
               canvas.renderAll();
               setImageTocanvas(data.value.annotationsUrl);
            });
         }         
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [canvas]);

   // ---
   React.useEffect(() => {
      if (canvas && data.value?.annotationsPassingUrl && data.value.annotationsPassingUrl !== data.value.annotationsUrl) {
         setImageTocanvas(data.value.annotationsPassingUrl);
         updateNodeValue(id, setNodes, { annotationsPassingUrl: '', annotationsUrl: data.value.annotationsPassingUrl });
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [canvas, data.value?.annotationsPassingUrl, data.value?.annotationsUrl]);

   // ---
   React.useEffect(() => {
      if (canvas) {
         canvas.on('mouse:down', startDrawing);
         canvas.on('mouse:move', drawRectangle);
         canvas.on('mouse:up', stopDrawing);
         // Properly cleanup the event listeners
         return () => {
            canvas.off('mouse:down', startDrawing);
            canvas.off('mouse:move', drawRectangle);
            canvas.off('mouse:up', stopDrawing);
         };
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [canvas, startX, startY, rect, annotations]);
   
   // ---
   const startDrawing = (o: any) => {
      if (!canvas || !annotations.isCanDrawing) return;
      setAnnotations(prev => ({ ...prev, isDrawing: true }));
      const pointer = canvas.getPointer(o.e);
      setStartX(pointer.x);
      setStartY(pointer.y);
      const newRect = new fabric.Rect({ 
         left: pointer.x, top: pointer.y, width: 0, height: 0, fill: 'transparent', stroke: '#1E272E', strokeWidth: 2,
         lockScalingX: true, lockScalingY: true, lockRotation: true, hasControls: false,
      });
      setRect(newRect);
      canvas.add(newRect);
   }
   const drawRectangle = (o: any) => {
      if (!annotations.isDrawing || !rect || !canvas) return;
      const pointer = canvas.getPointer(o.e);
      const updatedRect = { ...rect };
   
      if (startX > pointer.x) updatedRect.left = pointer.x;
      if (startY > pointer.y) updatedRect.top = pointer.y;
   
      updatedRect.width = Math.abs(startX - pointer.x);
      updatedRect.height = Math.abs(startY - pointer.y);
      // Instead of spreading the updatedRect, update each property individually
      rect.set({ left: updatedRect.left, top: updatedRect.top, width: updatedRect.width, height: updatedRect.height });
      canvas.renderAll();
   }
   const stopDrawing = async () => {
      if (!annotations.isDrawing) return;
      setAnnotations(prev => ({ ...prev, isDrawing: false, isCanDrawing: false }));
      if (!data.value.annotationsLabel || typeof data.value.annotationsLabel !== 'string') return;

      const textLeft = rect.left;
      const textTop = rect.top - 35;
      const backgroundRect = new fabric.Rect({
         left: textLeft - 5, top: textTop - 5, width: data.value.annotationsLabel.length * 12, 
         height: 33, fill: 'rgb(30, 39, 46)', stroke: 'transparent', rx: 10, ry: 10,
         // lockScalingX: true, lockScalingY: true, lockRotation: true, hasControls: false,
      });
      const text = new fabric.Text(data.value.annotationsLabel, { left: textLeft, top: textTop, fontSize: 20, fill: '#fff' });
      if (canvas) {
         const group = new fabric.Group([backgroundRect, text], { left: textLeft - 5, top: textTop - 5 });
         canvas.add(group);
         rect.set('label', data.value.annotationsLabel);

         const originalToObject = rect.toObject;
         rect.toObject = function() {
            return Object.assign(originalToObject.call(this), {
               label: this.get('label')
            });
         };
         canvas.forEachObject(obj => obj.set({ hasControls: false }));
         canvas.renderAll();
         // @ts-ignore
         await getURLfromBase64(document.querySelector(`#${data.index}`)?.toDataURL(), data.index).then(url => {
            updateNodeValue(id, setNodes, { canvasJSON: JSON.stringify(canvas.toJSON()), canvasURLimg: url });
         });
      } 
   }

   // ---
   const handleInputImageChange = async (event: React.ChangeEvent<HTMLInputElement> | React.DragEvent<HTMLDivElement>) => {
      const target = event.target as HTMLInputElement;

      if (target.files && target.files[0]) {
         const reader = new FileReader();
         reader.onload = (e) => {
            const result = e.target?.result;
            if (typeof result === 'string') {
               setImageTocanvas(result, () => {
                  updateNodeValue(id, setNodes, { isAnnotationsFile: true });
               });
            }
         }
         reader.readAsDataURL(target.files[0]);
         const request = new FormData();
         request.append('file', target.files[0]);
         const response = (await axios.post(`${webURL}/general/uploader`, request, { headers: { 'Content-Type': 'multipart/form-data', 'x-auth-token': userToken }})).data;
         updateNodeValue(id, setNodes, { annotationsUrl: response.url });
         target.value = '';
      }
   }

   // ---
   const setImageTocanvas = (source: string, callback?: () => void) => {
      const imgObj = new Image();
      imgObj.crossOrigin = 'Anonymous';
      imgObj.src = source;

      imgObj.onload = () => {
         const image = new fabric.Image(imgObj);
         if (!canvas) return;
         const imageAspectRatio = imgObj.width / imgObj.height;
         const canvasAspectRatio = canvas.width / canvas.height;
         let scaleFactor;
         if (canvasAspectRatio > imageAspectRatio) scaleFactor = canvas.height / imgObj.height;
         else scaleFactor = canvas.width / imgObj.width;
         
         image.scale(scaleFactor);
         image.set({ left: (canvas.width - image.getScaledWidth()) / 2, top: (canvas.height - image.getScaledHeight()) / 2 });
         canvas.backgroundImage = image;
         canvas.renderAll();
         if (callback) callback();
      }
   }

   // ---
   const clearAllAnnotations = (isWithImage = false) => {    
      if (!canvas) return;  
      if (!isWithImage) {
         const objects = canvas.getObjects();
         const rectangles = objects.filter(obj => obj.type === 'rect');
         const groups = objects.filter(obj => obj.type === 'group');
         rectangles.forEach(rect => canvas.remove(rect));
         groups.forEach(group => canvas.remove(group));
         setRect(null);
         canvas.renderAll();
         updateNodeValue(id, setNodes, { canvasJSON: null });
      } else {
         if (inputRef.current) inputRef.current.value = '';
         updateNodeValue(id, setNodes, { canvasJSON: null, canvasURLimg: null, isAnnotationsFile: false, annotationsUrl: '' });
         canvas.clear(); 
         canvas.renderAll();
      }
   }
   // ---
   const zoomToPoint = (newZoom: number) => {
      if (!canvas) return;
      const canvasWidth = canvas.getWidth();
      const canvasHeight = canvas.getHeight();
      const centerPoint = new fabric.Point(canvasWidth / 2, canvasHeight / 2);
  
      canvas.zoomToPoint(centerPoint, newZoom);
      updateNodeValue(id, setNodes, { zoom: newZoom });
   }

   // ---
   const handleSelectTypes = (selectId: number) => {
      setTypes((types) => types.map((type) => {
         if (type.id === selectId) return { ...type, isActive: true };
         else return { ...type, isActive: false };
      }));
      const refId = types.find(obj => obj.id === selectId)?.refId;
      setNodes(nodes => nodes.map(node => {
         if (node.id === id) {
            return { ...node, data: { ...node.data, value: { ...node.data.value, type: refId, isAnnotationsToggle: false }}}
         } else return node;
      }));
   }

   // ---
   const isWarning = false;

   return (<>
      <SettingsBlock nodeId={ id }data={ settings } setData={ setSettings } />
      {!data.value?.isNoMinimize ? (isWarning ? <>
         <div id="warning"></div>
         <span id="warningIcon"><IoWarningOutline size={21} /></span>
      </> : data.output ? <>
         <div id="ran"></div>
         <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
      </> : null) : null}
      <Main $isNoMinimize={ data.value?.isNoMinimize }>
         { data.value?.isNoMinimize ? <>
            {isWarning ? <div id="warning"></div> : data.output ? <div id="ran"></div> : null }
            {data.value?.type === 'check_id' ? <button className="btn1 line" style={{ backgroundColor: data.background }} onClick={_ => updateNodeValue(id, setNodes, { isAnnotationsToggle: !data.value?.isAnnotationsToggle })}>
               <FontAwesomeIcon icon={data.value?.isAnnotationsFile ? faCheckCircle : faComments} />
               <span>Create a Reference ID</span>
            </button> : null}
            <Select title="Function" data={types} handleSelectItem={handleSelectTypes} isSmall={true} />
         </> : null}
         <div 
            className={`annotations ${!data.value?.isAnnotationsFile ? 'noImage' : ''}`} 
            style={{ display: data.value?.isAnnotationsToggle ? 'flex' : 'none' }}
         >
            <h3 className="title" style={{ backgroundColor: data.background, color: '#fff' }}>
               <span>Annotations (Initial)</span>
               <FontAwesomeIcon icon={ faTimes } onClick={ _=> updateNodeValue(id, setNodes, { isAnnotationsToggle: false }) } />
            </h3>
            <div className="nodrag">
               <div className="top">
                  <div className="upload">
                     <input ref={ inputRef } type="file" onChange={ handleInputImageChange } />
                     <FontAwesomeIcon icon={ faCloudArrowUp } />
                     <span>Click to Upload</span>
                  </div>
                  { data.value?.isAnnotationsFile ? <>
                     <input placeholder="label" value={ data.value?.annotationsLabel || '' } onChange={ e => updateNodeValue(id, setNodes, { annotationsLabel: e.target.value }) } /> 
                     <button 
                        className={ !annotations.isCanDrawing ? 'disabled' : '' } 
                        onClick={ _=> setAnnotations(prev => ({ ...prev, isCanDrawing: !prev.isCanDrawing })) }
                        title="Add Rectangle"
                        style={{ backgroundColor: data.background }}
                     >
                        <FontAwesomeIcon icon={ faCropSimple } />
                     </button>
                     <button 
                        onClick={ _=> clearAllAnnotations() } 
                        title="Delete All Annotations"
                        style={{ backgroundColor: data.background }}
                     >
                        <FontAwesomeIcon icon={ faCrop } />
                     </button>
                        <button 
                           onClick={ _=> { 
                              const newZoom = (data.value.zoom || canvas?.getZoom()) + 0.1; 
                              zoomToPoint(newZoom);
                           }} 
                           title="Zoom In"
                           style={{ backgroundColor: data.background }}
                        >
                        <FontAwesomeIcon icon={ faPlus } />
                     </button>
                     <button 
                        onClick={ _=> {
                           const newZoom = (data.value.zoom || canvas?.getZoom()) - 0.1;
                           if (newZoom > 0) zoomToPoint(newZoom);
                        }} 
                        title="Zoom Out"
                        style={{ backgroundColor: data.background }}
                     >
                        <FontAwesomeIcon icon={ faMinus } />
                     </button>
                  </> : null }
                  <button 
                     onClick={ _=> clearAllAnnotations(true) } 
                     title="Clear All"
                     style={{ backgroundColor: data.background }}
                  >
                     <FontAwesomeIcon icon={ faRotateRight } />
                  </button>
               </div>
               <div className="container">
                  <canvas id={ data.index } ref={ canvasRef } />
                  { !data.value?.isAnnotationsFile ? <span>There is no image to create a template</span> : null }
               </div>
            </div>
         </div>
      </Main>
   </>);
}

// ---
async function getURLfromBase64(data: string, index: string) {
   const base64Response = await fetch(data);
   const blob = await base64Response.blob();
   
   const request = new FormData();
   request.append('file', blob, `${index}.png`);
   const response = (await axios.post(`${webURL}/general/uploader`, request)).data;
   return response.url;
}